import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Period, PeriodTypeWeek, PeriodYear, PeriodYearValues, getAllWeekValues, getCurrentWeek, getCurrentYear, getPeriodWeekValue, getStartOfWeek } from "@dto/period";
import { TW_ChoiceScrollArea, TW_CommonCommandInput } from "@utils/twClasses";
import { Square, SquareCheck } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PeriodEditionFormProps } from "./PeriodCell";

export const PeriodWeekAndYearEditionForm = memo(function PeriodWeekAndYearEditionForm({ field, value, updatePeriod, searchValue, setSearchValue, searchInputRef, displaySearch = true, hasValidation = true, disabled = false }: PeriodEditionFormProps) {

    const { t } = useTranslation();
    const [weekValue, setWeekValue] = useState(value?.week() ?? getCurrentWeek());
    const [yearValue, setYearValue] = useState(value?.year() ?? getCurrentYear());
    const [modified, setModified] = useState(value === undefined);
    const [allWeekValues, setAllWeekValues] = useState(() => {
        return getAllWeekValues(yearValue);
    });

    useEffect(() => {
        setAllWeekValues(getAllWeekValues(yearValue));
    }, [yearValue]);


    const onKeyDown = (event: React.KeyboardEvent) => {
        if (!setSearchValue || disabled) {
            return;
        }
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                setSearchValue("");
                break;
            case "Enter":
                if (searchValue === "") {
                    event.preventDefault();
                    event.stopPropagation();
                    validate(weekValue, yearValue);
                } else {
                    event.stopPropagation();
                    setSearchValue("");
                }
                break;
            case "Tab":
                event.preventDefault();
                validate(weekValue, yearValue);
                setSearchValue("");
                break;
        }
    }

    const onWeekChange = (weekValue: string) => {
        changeWeek(parseInt(weekValue));
    }

    const onWeekLabelChange = (weekLabel: string) => {
        const week = Period.weekValue(weekLabel, t);
        changeWeek(week);
    }

    function changeWeek(week: number) {
        if (week === weekValue) {
            return;
        }
        setWeekValue(week);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(week, yearValue);
        }
    }

    const onYearChange = (yearName: string) => {
        if (yearName === yearValue.toString()) {
            return;
        }
        const year = parseInt(yearName);
        setYearValue(year);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(weekValue, year);
        }
    }

    function validate(week: number | undefined, year: number | undefined) {
        if (disabled) {
            return;
        }
        if (week === value?.week() && year === value?.year()) {
            return;
        }
        if (!week || !year) {
            updatePeriod(undefined);
        } else {
            const newDate = getStartOfWeek(year, week);
            const newPeriod = Period.fromDate(newDate, PeriodTypeWeek);
            updatePeriod(newPeriod);
        }
        setModified(false);
    }

    const hasSearch = displaySearch && setSearchValue;

    const onValidateClick = () => {
        validate(weekValue, yearValue);
    }

    return (
        <div key={"filter-week-" + field?._key} className={"flex flex-col sm:flex-row flex-start items-stretch sm:items-start gap-2 " + (hasSearch ? " w-full " : " w-48 ")}>
            {hasSearch &&
                <div className="flex flex-col flex-start items-center gap-1 grow">
                    <Command onKeyDown={onKeyDown} className="min-w-0">
                        <CommandInput
                            placeholder={t("data.search.placeholder")}
                            className={TW_CommonCommandInput + " h-10 min-h-10"}
                            autoFocus={true}
                            value={searchValue}
                            onValueChange={(value) => setSearchValue(value)}
                            ref={searchInputRef}
                            disabled={disabled}
                        />
                        {searchValue === "" &&
                            <Select onValueChange={onWeekChange} value={weekValue.toString()} disabled={disabled}>
                                <SelectTrigger className="w-full grow mt-1 outline-none focus:outline-none">
                                    <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {allWeekValues.map((week: number) => {
                                        const weekLabel = getPeriodWeekValue(week, yearValue, false, false);
                                        const currWeekValue = week.toString();
                                        return (
                                            <SelectItem key={currWeekValue} value={currWeekValue} onSelect={() => onWeekChange(currWeekValue)}>{weekLabel}</SelectItem>
                                        )
                                    })}
                                </SelectContent>
                            </Select>
                        }
                        {searchValue !== "" &&
                            <div className={TW_ChoiceScrollArea}>
                                <CommandList className="h-full max-h-full w-full">
                                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                                    <CommandGroup>
                                        {allWeekValues.map((week: number) => {
                                            const weekLabel = getPeriodWeekValue(week, yearValue, false, false);
                                            return (
                                                <CommandItem key={week} value={weekLabel} onSelect={() => onWeekLabelChange(weekLabel)}>{weekLabel}</CommandItem>
                                            )
                                        })}
                                    </CommandGroup>
                                </CommandList>
                            </div>
                        }
                    </Command>
                    {/* } */}
                </div>
            }
            {!hasSearch &&
                <Select onValueChange={onWeekChange} value={weekValue.toString()} disabled={disabled}>
                    <SelectTrigger className={"h-9 grow outline-none focus:outline-none" + (hasSearch ? " w-24 min-w-24 " : " w-full")}>
                        <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                        {allWeekValues.map((week: number) => {
                            const weekLabel = getPeriodWeekValue(week, yearValue, false, false);
                            const currWeekValue = week.toString();
                            return (
                                <SelectItem key={currWeekValue} value={currWeekValue} onSelect={() => onWeekChange(currWeekValue)}>{weekLabel}</SelectItem>
                            )
                        })}
                    </SelectContent>
                </Select>
            }
            <div className="flex flex-col flex-start items-center gap-0 grow">
                {hasSearch &&
                    <div className="hidden sm:block w-full h-10 min-h-10 border-none border-transparent"></div>
                }
                <div className={"flex flex-row flex-start items-center gap-1" + (hasSearch ? " mt-1" : "")}>
                    <Select onValueChange={onYearChange} value={yearValue.toString()} disabled={disabled}>
                        <SelectTrigger>
                            <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                        </SelectTrigger>
                        <SelectContent>
                            {PeriodYearValues.map((year: PeriodYear) => {
                                const currYearValue = year.toString();
                                return (
                                    <SelectItem key={currYearValue} value={currYearValue} onSelect={() => onYearChange(currYearValue)}>{currYearValue}</SelectItem>
                                )
                            })}
                        </SelectContent>
                    </Select>
                    {hasValidation && modified && <Square onClick={onValidateClick} className="w-6 h-6 min-w-6 min-h-6 text-gray-500 cursor-pointer" />}
                    {hasValidation && !modified && <SquareCheck className="w-6 h-6 min-w-6 min-h-6 text-green-500 cursor-default" />}
                </div>
            </div>
        </div>
    )
}, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.value === nextProps.value && prevProps.searchValue === nextProps.searchValue && prevProps.searchInputRef === nextProps.searchInputRef && prevProps.disabled === nextProps.disabled;
});